import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

interface SummaryData {
  rules: { total: number; label: string };
  executions: {
    total: number;
    success: number;
    failures: number;
    successRate: number;
    avgDurationMs: number;
    label: string;
  };
  users: { total: number; active: number; label: string };
  apiKeys: { total: number; active: number; label: string };
  domains: { total: number; label: string };
}

interface TrendData {
  date: string;
  executions: number;
  success: number;
  failures: number;
  avgDuration: number;
  successRate: number;
}

interface RuleUsage {
  rule_id: string;
  rule_name: string;
  total_executions: number;
  total_success: number;
  total_failures: number;
  success_rate: number;
  avg_duration_ms: number;
}

const Dashboard: React.FC = () => {
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [trend, setTrend] = useState<TrendData[]>([]);
  const [topRules, setTopRules] = useState<RuleUsage[]>([]);
  const [errors, setErrors] = useState<RuleUsage[]>([]);
  const [slowestRules, setSlowestRules] = useState<RuleUsage[]>([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);

  useEffect(() => {
    loadDashboardData();
  }, [days]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [summaryRes, trendRes, topRes, errorsRes, perfRes] = await Promise.all([
        axios.get(API_BASE + '/dashboard/summary?days=' + days),
        axios.get(API_BASE + '/dashboard/execution-trend?days=' + days),
        axios.get(API_BASE + '/dashboard/top-rules?days=' + days + '&limit=5'),
        axios.get(API_BASE + '/dashboard/errors?days=' + days + '&limit=5'),
        axios.get(API_BASE + '/dashboard/performance?days=' + days + '&limit=5')
      ]);

      setSummary((summaryRes.data as any).summary);
      setTrend((trendRes.data as any).trend || []);
      setTopRules((topRes.data as any).topRules || []);
      setErrors((errorsRes.data as any).errors || []);
      setSlowestRules((perfRes.data as any).slowestRules || []);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const getMaxExecutions = () => {
    return Math.max(...trend.map(t => t.executions), 1);
  };

  if (loading && !summary) {
    return <div className="dashboard-loading">Loading dashboard...</div>;
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h2>Dashboard</h2>
        <div className="time-range-selector">
          <label>Time Range:</label>
          <select value={days} onChange={(e) => setDays(parseInt(e.target.value))}>
            <option value={1}>Last 24 hours</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button onClick={loadDashboardData} className="btn-refresh">
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="dashboard-summary">
          <div className="summary-card">
            <div className="summary-value">{summary.rules.total}</div>
            <div className="summary-label">{summary.rules.label}</div>
          </div>
          <div className="summary-card">
            <div className="summary-value">{formatNumber(summary.executions.total)}</div>
            <div className="summary-label">{summary.executions.label}</div>
            <div className="summary-subtext">
              {summary.executions.successRate}% success rate
            </div>
          </div>
          <div className="summary-card success">
            <div className="summary-value">{formatNumber(summary.executions.success)}</div>
            <div className="summary-label">Successful</div>
          </div>
          <div className="summary-card danger">
            <div className="summary-value">{formatNumber(summary.executions.failures)}</div>
            <div className="summary-label">Failed</div>
          </div>
          <div className="summary-card">
            <div className="summary-value">{summary.executions.avgDurationMs}ms</div>
            <div className="summary-label">Avg Duration</div>
          </div>
          <div className="summary-card">
            <div className="summary-value">{summary.users.active}/{summary.users.total}</div>
            <div className="summary-label">Active Users</div>
          </div>
        </div>
      )}

      {/* Execution Trend Chart */}
      <div className="dashboard-section">
        <h3>Execution Trend</h3>
        {trend.length > 0 ? (
          <div className="trend-chart">
            <div className="chart-bars">
              {trend.map((t, i) => (
                <div key={i} className="chart-bar-group">
                  <div className="chart-bar-container">
                    <div
                      className="chart-bar success"
                      style={{ height: (t.success / getMaxExecutions()) * 100 + '%' }}
                      title={'Success: ' + t.success}
                    />
                    <div
                      className="chart-bar failure"
                      style={{ height: (t.failures / getMaxExecutions()) * 100 + '%' }}
                      title={'Failures: ' + t.failures}
                    />
                  </div>
                  <div className="chart-label">{t.date.slice(5)}</div>
                </div>
              ))}
            </div>
            <div className="chart-legend">
              <span className="legend-item success">Success</span>
              <span className="legend-item failure">Failures</span>
            </div>
          </div>
        ) : (
          <p className="empty-state">No execution data available</p>
        )}
      </div>

      <div className="dashboard-grid">
        {/* Top Rules */}
        <div className="dashboard-section">
          <h3>Top Rules by Usage</h3>
          {topRules.length > 0 ? (
            <div className="rule-list">
              {topRules.map((rule, i) => (
                <div key={rule.rule_id} className="rule-list-item">
                  <span className="rule-rank">#{i + 1}</span>
                  <span className="rule-name" title={rule.rule_id}>
                    {rule.rule_name || rule.rule_id}
                  </span>
                  <span className="rule-executions">
                    {formatNumber(rule.total_executions)} exec
                  </span>
                  <span className={'rule-rate ' + (rule.success_rate >= 95 ? 'good' : rule.success_rate >= 80 ? 'warn' : 'bad')}>
                    {rule.success_rate}%
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="empty-state">No rule usage data</p>
          )}
        </div>

        {/* Rules with Errors */}
        <div className="dashboard-section">
          <h3>Rules with Errors</h3>
          {errors.length > 0 ? (
            <div className="rule-list">
              {errors.map((rule) => (
                <div key={rule.rule_id} className="rule-list-item error">
                  <span className="rule-name" title={rule.rule_id}>
                    {rule.rule_name || rule.rule_id}
                  </span>
                  <span className="rule-failures">
                    {rule.total_failures} failures
                  </span>
                  <span className="rule-rate bad">
                    {100 - (rule.success_rate || 0)}% fail
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="empty-state good">No errors</p>
          )}
        </div>

        {/* Slowest Rules */}
        <div className="dashboard-section">
          <h3>Slowest Rules</h3>
          {slowestRules.length > 0 ? (
            <div className="rule-list">
              {slowestRules.map((rule) => (
                <div key={rule.rule_id} className="rule-list-item">
                  <span className="rule-name" title={rule.rule_id}>
                    {rule.rule_name || rule.rule_id}
                  </span>
                  <span className={'rule-duration ' + (rule.avg_duration_ms > 100 ? 'slow' : '')}>
                    {Math.round(rule.avg_duration_ms)}ms
                  </span>
                  <span className="rule-executions">
                    {formatNumber(rule.total_executions)} exec
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="empty-state">No performance data</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
